<template>
  <section class="container mx-auto px-4" ref="showList">
    <template v-if="!readMoreDialog && shows">
      <div
        v-for="show in shows"
        :key="`show-${show.id}-${show.date}`"
        class="show border-b last:border-b-0 border-solid border-linear-subtle/20 py-8"
      >
        <div class="details block md:flex" :id="`${show.id}-${show.date}`">
          <div class="meta w-full md:w-1/4 md:m-5">
            <div class="show-image relative w-full mb-5 pt-66pc md:pt-100pc md:mb-0">
              <picture class="absolute top-0 left-0 w-full h-full object-cover">
                <source
                  :srcset="show.srcset.rectLg.src"
                  type="image/webp"
                  media="(max-width: 768px)"
                />
                <source :srcset="show.srcset.sqLg.src" type="image/webp" />
                <img :src="show.srcset.original.src" :alt="show.name" />
              </picture>
            </div>
          </div>

          <div class="performances whitespace-pre-line w-full md:w-3/4 md:m-5">
            <div class="flex justify-between items-center">
              <h3 class="text-3xl font-semibold font-heading" :id="`show-${show.id}-name`">
                {{ show.name }}
              </h3>

              <router-link
                :id="`show-${show.id}-date-${show.date}`"
                :to="{
                  name: 'showDetails',
                  params: { showId: show.id },
                  query: { date: show.date },
                }"
                @click="onShowDetailLinkClick(show)"
                aria-label="Go to Show Detail"
              >
                <LinkIcon class="w-6 h-6" />
              </router-link>
            </div>

            <div class="mb-6 uppercase text-base font-semibold mb-3 font-date text-secondary">
              <h4 class="day-of-week" :id="`show-${show.id}-${show.date}-desc`">
                {{ show.dateStr }}
              </h4>
            </div>

            <div class="text-sm md:text-base mb-2 font-sans" :id="`show-${show.id}-description`">
              <template v-if="show.readMore">
                <p class="inline">{{ show.shortDescription }}...</p>
                <span
                  :id="`read-more-dialog-open-${show.id}`"
                  @click="onReadMore(show)"
                  class="text-fg-disclaimer cursor-pointer"
                >
                  Read More
                </span>
              </template>
              <template v-else>
                <p>{{ show.description }}</p>
              </template>
            </div>

            <div class="flex flex-wrap justify-start gap-2 sm:gap-4">
              <PerformanceButton
                v-for="performance in show.performances"
                :key="performance.id"
                :performance="performance"
                :error="ui.error"
                @click="saveScrollPosition"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="mt-4 flex justify-center">
        <Spinner :display="loading" />
      </div>
    </template>
    <template v-if="readMoreDialog && currentShow">
      <div
        class="read-more-dialog bg-body-checkout-sidebar text-fg-g-primary fixed md:relative z-20 overflow-y-scroll inset-0 h-full w-full p-4"
      >
        <div class="flex justify-between">
          <div
            :id="`read-more-dialog-title-${currentShow.id}`"
            class="text-xl md:text-3xl font-semibold font-heading mb-4"
          >
            {{ currentShow.name }}
          </div>
          <span
            :id="`read-more-dialog-close-${currentShow.id}-${currentShow.date}`"
            class="cursor-pointer"
          >
            <xCloseGuest @click="readMoreDialogClose" />
          </span>
        </div>
        <p id="read-more-dialog-description" class="show-description whitespace-pre-line">
          {{ currentShow.description }}
        </p>
        <span @click="readMoreDialogClose" class="md:hidden block mt-4 text-center cursor-pointer">
          <span
            class="read-more-close-button inline-block py-1 px-4 rounded text-fg-primary bg-surface-primary"
          >
            Close
          </span>
        </span>
      </div>
    </template>
  </section>
</template>

<style lang="scss" scoped>
@media screen and (min-width: 768px) {
  .mobile-only {
    display: none;
  }
}

@media screen and (max-width: 767px) {
  .mobile-hidden {
    display: none;
  }
}
</style>

<script lang="ts">
import moment from 'moment'
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import LinkIcon from '@/assets/link.svg?component'
import xCloseGuest from '@/assets/xCloseGuest.svg?component'
import Spinner from '@/common/components/atoms/Spinner.vue'
import PerformanceButton from '@/components/performance/PerformanceButton.vue'
import { StorePerformance, StoreShow } from '@/models'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { blankError } from '@/utils/errors'
import { sortBy, todayIsoStr } from '@/utils/utils'

interface DisplayShow extends StoreShow {
  performances: StorePerformance[]
  date: string
  datetime: string
  dateStr: string
  position: number[]
  shortDescription: string
  readMore?: boolean
}

export default defineComponent({
  name: 'SetShow',
  components: {
    PerformanceButton,
    Spinner,
    xCloseGuest,
    LinkIcon,
  },
  data() {
    return {
      onScroll: () => {},
      scrollY: '',
      loading: false,
      total: 0,
      currentPage: 1,
      currentShow: null as DisplayShow | null,
      pageSize: 5,
      readMoreDialog: false,
      ui: {
        error: blankError(),
      },
    }
  },

  beforeMount() {
    if (window) {
      this.onScroll = () => {
        if (!this.$refs.showList || this.loading || this.currentPage * this.pageSize >= this.total)
          return

        const scrollY = window.scrollY,
          pageHeight = window.innerHeight
        const totalHeight = (this.$refs.showList as Element).clientHeight

        if (totalHeight - scrollY <= pageHeight + 50) {
          this.loading = true
          this.currentPage++

          this.performanceStore
            .getPerformances({ page: this.currentPage, booking: 'true' })
            .then(() => {
              this.$nextTick(() => {
                this.loading = false
                this.onScroll()
              })
            })
        }
      }

      window.addEventListener('scroll', this.onScroll)
      this.$nextTick(() => this.onScroll())
    }
  },

  beforeUnmount() {
    if (window) {
      window.removeEventListener('scroll', this.onScroll)
    }
  },

  async created() {
    this.settingStore.ssrTitle = `Shows - ${this.settingStore.venue_name}`
  },

  watch: {
    async $route() {
      await this.init()
    },
  },

  computed: {
    ...mapStores(useSettingStore, useCheckoutStore, usePerformanceStore),

    performances(): StorePerformance[] {
      return this.performanceStore.performances.filter(p =>
        p.publish_datetime === null ? true : moment(p.publish_datetime).isSameOrBefore(moment()),
      )
    },

    shows(): DisplayShow[] {
      const shows: DisplayShow[] = []
      const current = todayIsoStr()

      for (const performance of this.performances) {
        if (performance.date < current) continue

        const show = shows.find(
          show => show.date == performance.date && show.id == performance.show_id,
        )

        if (show) {
          show.performances.push(performance)
          sortBy(show.performances, 'datetime')
        } else {
          const desc = performance.show?.description || ''
          shows.push({
            ...performance.show,
            id: performance.show_id,
            date: performance.date,
            datetime: performance.datetime,
            dateStr: this.settingStore.dateMedStr(performance.datetime),
            description: desc,
            position: performance.show?.position || [],
            performances: [performance],
            shortDescription: desc.split(' ').splice(0, 90).join(' '),
            readMore: desc.split(' ').length > 90,
          })
        }
      }
      return sortBy(shows, 'datetime')
    },
  },

  async mounted() {
    await this.init()
    this.checkoutStore.performance = null
    this.scrollToShow()
  },

  methods: {
    async init() {
      const response = await this.performanceStore.getPerformances({ booking: 'true' })

      this.currentPage = 1
      this.total = response.total
      this.pageSize = response.pageSize
    },

    onReadMore(show: DisplayShow) {
      this.readMoreDialog = true
      this.currentShow = show
      this.saveScrollPosition(show.performances[0])
    },

    readMoreDialogClose() {
      this.readMoreDialog = false
      this.currentShow = null
      // This setTimeout is needed to prevent the scroll from happening before the dialog fully closes
      setTimeout(this.scrollToShow, 0)
    },

    onShowDetailLinkClick(show: DisplayShow) {
      const performance = show.performances[0]
      this.saveScrollPosition(performance)
    },

    saveScrollPosition(performance: StorePerformance) {
      const performanceContainerElement = document
        .getElementById(`performance-${performance.id}`)
        ?.closest('.performances')
      if (!performanceContainerElement) return
      const { top } = performanceContainerElement.getBoundingClientRect()
      const topFromWindow = top + window.scrollY
      this.checkoutStore.setScrollPositionFromSetShow(topFromWindow)
    },

    scrollToShow() {
      if (this.checkoutStore.scrollPositionFromSetShow) {
        window.scrollTo({ top: this.checkoutStore.scrollPositionFromSetShow - 100 })
        this.checkoutStore.setScrollPositionFromSetShow(null)
      }
    },
  },
})
</script>
